import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Badge, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSlidersH, faTimes, faDollarSign, faSortAmountDown, faSortAmountUp, faSort } from '@fortawesome/free-solid-svg-icons';
import { useSearchParams } from 'react-router-dom';
import { db } from 'config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import '../css/advancedProducts.css';

/**
 * Preset-to-API-params mapping. Shared with AdvancedProductsGrid so
 * search, filter, and sort handlers all resolve presets the same way.
 */
export const PRESET_API_PARAMS = {
    notInShopify: { shopify: 'not_in_shopify' },
    inShopify: { shopify: 'in_shopify' },
    needsEnrichment: { enrichment: 'needs_enrichment' },
    hasImages: { images: 'has_images' },
    hasTags: { tags: 'has_tags' },
    noPricingTemplate: { pricingTemplate: 'missing' }
};

/**
 * Filter presets for common product filtering scenarios.
 * Each preset uses API parameters format for server-side filtering via productSearch.
 * This enables filtering of 45k+ products at the database level.
 */
const FILTER_PRESETS = {
    notInShopify: {
        label: 'Not in Shopify',
        icon: faFilter,
        apiParams: PRESET_API_PARAMS.notInShopify
    },
    inShopify: {
        label: 'In Shopify',
        icon: faFilter,
        apiParams: PRESET_API_PARAMS.inShopify
    },
    needsEnrichment: {
        label: 'Needs Enrichment',
        icon: faFilter,
        apiParams: PRESET_API_PARAMS.needsEnrichment
    },
    hasImages: {
        label: 'Has Images',
        icon: faFilter,
        apiParams: PRESET_API_PARAMS.hasImages
    },
    hasTags: {
        label: 'Has Tags',
        icon: faFilter,
        apiParams: PRESET_API_PARAMS.hasTags
    },
    noPricingTemplate: {
        label: 'No Pricing Template',
        icon: faDollarSign,
        apiParams: PRESET_API_PARAMS.noPricingTemplate
    }
};

const SORT_OPTIONS = {
    '': { label: 'Default (SKU)', icon: faSort },
    newest: { label: 'Newest First', icon: faSortAmountDown },
    oldest: { label: 'Oldest First', icon: faSortAmountUp },
};

/**
 * FilterPresetBar - One-click filter presets for product grid
 *
 * Provides quick access to common filter scenarios like "Not in Shopify"
 * using server-side filtering via productSearch API.
 * Also provides access to AG-Grid's Advanced Filter Builder for client-side
 * filtering on the currently loaded page of data.
 *
 * @param {Object} props
 * @param {React.RefObject} props.gridRef - Reference to AG-Grid (optional, for Advanced Filter Builder)
 * @param {string|null} props.activePreset - Currently active preset key
 * @param {Function} props.onPresetChange - Callback when preset changes
 * @param {Function} props.onFilterChange - Callback that receives API params object for server-side filtering
 * @param {Function} props.onSortChange - Callback when sort order changes
 * @param {string} props.activeSort - Currently active sort key ('', 'newest', 'oldest')
 */
export const FilterPresetBar = ({
    gridRef,
    activePreset,
    onPresetChange,
    onFilterChange,
    onSortChange,
    activeSort = ''
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [urlTemplateName, setUrlTemplateName] = useState(null);

    // Check for pricingTemplateId in URL on mount and apply filter
    useEffect(() => {
        const templateId = searchParams.get('pricingTemplateId');
        if (templateId) {
            // Apply the filter from URL
            onFilterChange?.({ pricingTemplateId: templateId });

            // Fetch template name for display
            const fetchTemplateName = async () => {
                try {
                    const templateDoc = await getDoc(doc(db, 'systemTemplates', templateId));
                    if (templateDoc.exists()) {
                        setUrlTemplateName(templateDoc.data().name);
                    } else {
                        setUrlTemplateName('Unknown Template');
                    }
                } catch (error) {
                    console.error('Error fetching template name:', error);
                    setUrlTemplateName('Template');
                }
            };
            fetchTemplateName();
        }
    }, [searchParams, onFilterChange]);

    // Clear URL-based template filter
    const clearUrlFilter = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('pricingTemplateId');
        setSearchParams(newParams);
        setUrlTemplateName(null);
        onFilterChange?.({});
        onPresetChange?.(null);
    };

    /**
     * Apply a filter preset using server-side filtering
     * @param {string} presetKey - Key of preset to apply
     */
    const applyPreset = (presetKey) => {
        const preset = FILTER_PRESETS[presetKey];
        if (!preset) {
            console.warn(`FilterPresetBar: Unknown preset "${presetKey}"`);
            return;
        }

        // If clicking the same preset, toggle it off
        if (activePreset === presetKey) {
            clearFilters();
            return;
        }

        // Call the API filter callback for server-side filtering
        onFilterChange?.(preset.apiParams);
        onPresetChange?.(presetKey);
    };

    /**
     * Clear all filters and reset active preset
     */
    const clearFilters = () => {
        onFilterChange?.({});  // Clear all API filters
        onPresetChange?.(null);
    };

    /**
     * Open AG-Grid's Advanced Filter Builder modal
     */
    const openAdvancedFilterBuilder = () => {
        if (!gridRef?.current?.api) {
            console.warn('FilterPresetBar: Grid API not available');
            return;
        }

        gridRef.current.api.showAdvancedFilterBuilder();
    };

    const currentSort = SORT_OPTIONS[activeSort] || SORT_OPTIONS[''];

    return (
        <div className="filter-preset-bar">
            <span className="filter-label me-2" style={{ fontSize: '0.85rem', color: '#5e6e82' }}>
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Quick Filters:
            </span>

            <ButtonGroup className="me-2">
                {Object.entries(FILTER_PRESETS).map(([key, preset]) => (
                    <Button
                        key={key}
                        variant={activePreset === key ? 'primary' : 'outline-primary'}
                        className="preset-btn"
                        onClick={() => applyPreset(key)}
                        title={preset.label}
                    >
                        <FontAwesomeIcon icon={preset.icon} className="me-1" />
                        {preset.label}
                    </Button>
                ))}
            </ButtonGroup>

            {/* Sort by Date Added dropdown */}
            <Dropdown as={ButtonGroup} className="me-2">
                <Dropdown.Toggle
                    variant={activeSort ? 'primary' : 'outline-secondary'}
                    className="preset-btn"
                    title="Sort by date added to catalog"
                >
                    <FontAwesomeIcon icon={currentSort.icon} className="me-1" />
                    {currentSort.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {Object.entries(SORT_OPTIONS).map(([key, opt]) => (
                        <Dropdown.Item
                            key={key || 'default'}
                            active={activeSort === key}
                            onClick={() => onSortChange?.(key)}
                        >
                            <FontAwesomeIcon icon={opt.icon} className="me-1" />
                            {opt.label}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>

            <Button
                variant="outline-secondary"
                className="preset-btn me-2"
                onClick={openAdvancedFilterBuilder}
                title="Advanced Filter Builder (filters current page only)"
            >
                <FontAwesomeIcon icon={faSlidersH} className="me-1" />
                Advanced...
            </Button>

            {/* URL-based template filter indicator */}
            {urlTemplateName && (
                <Badge bg="info" className="me-2 d-flex align-items-center" style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                    <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                    Template: {urlTemplateName}
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2 text-white"
                        onClick={clearUrlFilter}
                        title="Clear template filter"
                        style={{ textDecoration: 'none' }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </Button>
                </Badge>
            )}

            {(activePreset || urlTemplateName) && !urlTemplateName && (
                <Button
                    variant="outline-danger"
                    className="preset-btn"
                    onClick={clearFilters}
                    title="Clear all filters"
                >
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Clear
                </Button>
            )}
        </div>
    );
};

export default FilterPresetBar;
